import { JSX } from "react";
import "../../../../App.css";
import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IPostUserResponse, IdentityProvider, User } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import { IRouterProps, withRouting } from "@iconics/components";
import { Button, ButtonBar, Card, DeleteButton, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextArea } from "@iconics/components";
import EditUserDetails from "./EditUserDetails";
import EditUserRoles from "./EditUserRoles";
import { Validator } from '@iconics/core';
import EditUserConfiguration from "./EditUserConfiguration";
import { IPatchUser } from "../../../../Providers.FadxPlatformApiClientV2/FadxPlatformApiClientV2";
import { UserType } from "../../../../Providers.StaticData/StaticDataProvider";
import { IUserDetails } from "../../../../Providers.LocalStorage/Models";

class EditUser extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private identityProviders: IdentityProvider[] = [];
    private get alert() { return appContext().alert; }
    private get labels() { return appContext().labels; }

    private showPassword(props: IProps = this.props, state: IState = this.state): boolean
    {
        return props.params.id === '0' && this.state.identityProviderId.toString() === '1' && state.userType === 'User';
    }

    private get isNative(): boolean
    {
        return this.state.identityProviderId.toString() === '1';
    }

    private get userId(): string
    {
        return (this.isNew ? '' : this.props.params.id ?? '');
    }

    private get isNew(): boolean
    {
        return this.props.params.id === '0';
    }

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            showResetPasswordDialog: false,
            canUpdate: false,
            canDelete: false,
            canViewUserPreferences: false,
            sections: [],

            email: '',
            emailError: '',
            identityProviderOptions: [],
            identityProviderId: 0,
            userTypeOptions: [],
            userType: 'User',
            preferredName: '',
            firstName: '',
            lastName: '',
            displayName: '',
            firstNameError: '',
            lastNameError: '',
            displayNameError: '',
            initials: '',
            phoneNumber: '',
            location: '',
            password: '',
            passwordError: '',
            applicationKey: '',
            isFullApplicationKey: false,
            userPreferences: '',
            userPreferencesError: '',
            etag: '',
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_User_PageTitle;
        this.backUrl = `/security-users`;

        await this.loading(async () =>
        {
            await this.loadIdentityProviders();
            await this.loadUserTypes();
            await this.handleIdChange();
        });

        this.validator
            .addInput('email', () => this.state.email, i => this.setState({ emailError: i }))
            .isRequired()
            .isValidEmail();

        this.validator
            .addInput('displayName', () => this.state.displayName, i => this.setState({ displayNameError: i }))
            .isRequired();

        this.validator
            .addInput('firstName', () => this.state.firstName, i => this.setState({ firstNameError: i }))
            .isRequired();

        this.validator
            .addInput('lastName', () => this.state.lastName, i => this.setState({ lastNameError: i }))
            .isRequired();

        this.validator
            .addInput('userPreferences', () => this.state.userPreferences, i => this.setState({ userPreferencesError: i }))
            .customRule(this.validateUserPreferences, { errorLabel: this.labels.Platform_Users_Edit_UserPreferencesInvalid })

        this.setPasswordValidator();
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState): Promise<void>
    {
        if (prevProps.params.id !== this.props.params.id && !this.isNew)
        {
            await this.loading(async () => this.handleIdChange());
        }
        if (this.showPassword(prevProps, prevState) !== this.showPassword())
        {
            this.setPasswordValidator();
        }
    }

    private async handleIdChange(): Promise<void>
    {
        if (!this.isNew)
        {
            await this.loadUser();
        }

        this.setState({
            canUpdate: this.isNew || appContext().localStorageProvider.hasRight('ADMINPORTAL.Users.Update'),
            canDelete: this.isNew || appContext().localStorageProvider.hasRight('ADMINPORTAL.Users.Delete'),
            canViewUserPreferences: appContext().localStorageProvider.hasRight("ADMINPORTAL.Users.ShowUserPreferences"),
        });
    }

    private setPasswordValidator(): void
    {
        this.validator.removeInput('password');
        if (this.showPassword())
        {
            this.validator
                .addInput('password', () => this.state.password, i => this.setState({ passwordError: i }))
                .isRequired();
        }
    }

    private async loadIdentityProviders(): Promise<void>
    {
        const identityProvidersData = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.get<PagedResponse<IdentityProvider[]>>({
            select: IdentityProvider,
            tenancy: appContext().tenantId,
        });

        this.identityProviders = identityProvidersData.value;

        // we only show native identity providers for selection
        const identityProviderOptions = this.identityProviders.filter(i => i.SecurityProviderTypeId === 0).map(i => ({ value: i.Id.toString(), label: i.Name }));

        await this.setStateAsync({
            identityProviderOptions: identityProviderOptions,
            identityProviderId: identityProviderOptions.length > 0 ? parseInt(identityProviderOptions[0].value) : 1,
        });
    }

    private async loadUserTypes(): Promise<void>
    {
        const userTypeOptions = [
            { value: 'User' as UserType, label: this.labels.User_UserType_User },
            { value: 'App' as UserType, label: this.labels.User_UserType_Application },
        ];

        await this.setStateAsync({
            userTypeOptions: userTypeOptions,
            userType: 'User',
        });
    }

    private async loadUser(): Promise<void>
    {
        const user = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.get<User>({
            select: User,
            tenancy: appContext().tenantId,
            userid: this.userId,
        });

        const state = this.props.location.state as ILocationState | undefined;

        await this.setStateAsync({
            email: user.Email,
            identityProviderId: user.IdentityProviderId,
            userType: user.UserType,
            applicationKey: state?.applicationKey || user.ApplicationKey || '',
            isFullApplicationKey: !!state?.applicationKey,
            preferredName: user.PreferredName ?? '',
            initials: user.UserInitials ?? '',
            firstName: user.FirstName ?? '',
            lastName: user.LastName ?? '',
            displayName: user.DisplayName ?? '',
            phoneNumber: user.PhoneNumber ?? '',
            location: user.UserLocation ?? '',
            userPreferences: user.UserPreferences ?? '',
            etag: user["@odata.etag"],
        });

        if (state?.userCreated)
        {
            this.props.navigate('.', { replace: true, state: null });
        }

        // add identity provider to options if not present (only native idp are present by default)
        if (this.state.identityProviderOptions.find(i => i.value === user.IdentityProviderId.toString()) === undefined)
        {
            const identityProvider = this.identityProviders.find(i => i.Id === user.IdentityProviderId);
            await this.setStateAsync({
                identityProviderOptions:
                    [{ label: identityProvider?.Name ?? user.IdentityProviderId.toString(), value: user.IdentityProviderId.toString() },
                    ...this.state.identityProviderOptions]
            });
        }
    }


    private async handleResetPassword(): Promise<void>
    {
        await this.loading(async () =>
        {
            try
            {
                await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.resetpassword.put({
                    tenancy: appContext().tenantId,
                    userid: this.state.email,
                });
            }
            catch
            {
                return;
            }
            finally
            {
                this.setState({ showResetPasswordDialog: false });
            }
            this.alert.show(this.labels.Platform_Common_Success, this.labels.Platform_Users_Edit_PasswordResetSuccess);
        });
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.delete({
                tenancy: appContext().tenantId,
                userid: this.userId,
            });
            this.props.navigate("/security-users");
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () => 
        {
            try
            {
                await (this.isNew ? this.createUser() : this.updateUser());
            }
            catch (error)
            {
                console.error(error)
            }
        });
    }


    private validateUserPreferences(value: string): boolean
    {
        if (value.trim() === '')
        {
            return true;
        }
        try
        {
            JSON.parse(value.replaceAll("\r\n", ""));
            return true;
        }
        catch
        {
            return false;
        }
    }

    private async createUser(): Promise<void>
    {
        const response = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.post<IPostUserResponse>({
            tenancy: appContext().tenantId,
            body: {
                Email: this.state.email,
                IdentityProviderId: this.state.identityProviderId,
                UserType: this.state.userType === 'User' ? 0 : 1,
                PreferredName: this.state.preferredName,
                UserInitials: this.state.initials,
                FirstName: this.state.firstName,
                LastName: this.state.lastName,
                DisplayName: this.state.displayName,
                PhoneNumber: this.state.phoneNumber,
                UserLocation: this.state.location,
                Password: (this.showPassword() ? this.state.password : null),
                UserPreferences: this.state.userPreferences,
            },
        });

        this.props.navigate(`/security-users/userdetails/${response.Id}`, {
            replace: true,
            state:
                {
                    userCreated: true,
                    applicationKey: response.ApplicationKey,

                } satisfies ILocationState
        });
    }

    private async updateUser(): Promise<void>
    {
        let body: IPatchUser;
        if (this.isNative)
        {
            body = {
                Email: this.state.email,
                DisplayName: this.state.displayName,
                FirstName: this.state.firstName,
                LastName: this.state.lastName,
                PreferredName: this.state.preferredName,
                UserInitials: this.state.initials,
                PhoneNumber: this.state.phoneNumber,
                UserLocation: this.state.location,
                ConcurrencyStamp: this.state.etag,
                Deleted: false,
            };
        }
        else
        {
            body = {
                PreferredName: this.state.preferredName,
                UserInitials: this.state.initials,
                ConcurrencyStamp: this.state.etag,
            };
        }

        await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.byUserid.patch({
            tenancy: appContext().tenantId,
            userid: this.userId,
            body: body,
        });

        this.updateUserIfCurrent();

        this.props.navigate("/security-users");
    }
    
    private updateUserIfCurrent(): void
    {
        // if the updated user is the currently logged in user,
        // we must update the displayName that will go into the user menu (#25437)
        const localUser = appContext().localStorageProvider.getUserDetails();

        if (localUser.email.toLocaleLowerCase() === this.state.email.toLocaleLowerCase()) {
            const userDetail: IUserDetails = {
                ...localUser,
                displayName: this.getUpdatedDisplayName() ?? localUser.displayName,
            };
            appContext().localStorageProvider.setUserDetails(userDetail);
        }
    }

    private getUpdatedDisplayName(): string
    {
            if (this.state.preferredName?.trim())
            {
                return this.state.preferredName.trim();
            }

            if (this.state.displayName?.trim())
            {
                return this.state.displayName.trim();
            }

            if (this.state.firstName?.trim() && this.state.lastName?.trim())
            {
                return `${this.state.firstName.trim()} ${this.state.lastName.trim()}`;
            }

            if (this.state.firstName?.trim())
            {
                return this.state.firstName.trim();
            }

            if (this.state.lastName?.trim())
            {
                return this.state.lastName.trim();
            }

            return this.state.email;
    }

    private handleRoleClick(id: string): void
    {
        this.props.navigate(`/security-roles/rolesdetails/${id}?user-id=${this.userId}`);
    }

    private handleInputChange<K extends keyof IState>(fieldName: K, value: IState[K]): void
    {
        this.setState({ [fieldName]: value } as Pick<IState, K>, () =>
        {
            this.validator.validate(fieldName);
        });
    }

    private renderDetails(): JSX.Element
    {
        return (
            <EditUserDetails
                email={this.state.email}
                emailError={this.state.emailError}
                preferredName={this.state.preferredName}
                firstName={this.state.firstName}
                lastName={this.state.lastName}
                displayName={this.state.displayName}
                firstNameError={this.state.firstNameError}
                lastNameError={this.state.lastNameError}
                displayNameError={this.state.displayNameError}
                initials={this.state.initials}
                phoneNumber={this.state.phoneNumber}
                location={this.state.location}
                isNew={this.isNew}
                isNative={this.isNative}
                handleInputChange={(fieldName, e) => this.handleInputChange(fieldName, e)}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
            />
        )
    }

    private renderConfiguration(): JSX.Element
    {
        return (
            <EditUserConfiguration
                identityProviderOptions={this.state.identityProviderOptions}
                identityProviderId={this.state.identityProviderId}
                userTypeOptions={this.state.userTypeOptions}
                userType={this.state.userType}
                showPassword={this.showPassword()}
                password={this.state.password}
                passwordError={this.state.passwordError}
                applicationKey={this.state.applicationKey}
                isFullApplicationKey={this.state.isFullApplicationKey}
                isNew={this.isNew}
                isNative={this.isNative}
                handleInputChange={(fieldName, e) => this.handleInputChange(fieldName, e)}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
            />
        )
    }

    private renderPreferences(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_User_PreferencesTitle}
                subTitle={this.labels.Platform_User_PreferencesSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
            >
                <TextArea
                    disabled={true}
                    name="userPreferences"
                    rows={15}
                    label={this.labels.User_UserPreferences}
                    placeholder={this.labels.User_UserPreferences}
                    value={this.state.userPreferences}
                    onChange={e => this.setState({ userPreferences: e.target.value })}
                    invalid={!!this.state.userPreferencesError}
                    invalidMessage={this.state.userPreferencesError}
                />
            </Card>
        );
    }
    private renderRoles(): JSX.Element
    {
        return (
            <EditUserRoles
                userId={this.props.params.id ?? ''}
                onRoleClick={e => this.handleRoleClick(e)}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 2) }))}
            />
        );
    }

    public render(): JSX.Element
    {
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <GridContainer
                            spacing={5}
                        >
                            <GridItem>
                                {this.renderDetails()}
                            </GridItem>
                            <GridItem>
                                {this.renderConfiguration()}
                            </GridItem>
                            <GridItem>
                                {this.renderPreferences()}
                            </GridItem>
                            {
                                !this.isNew &&
                                <>
                                    <GridItem>
                                        {this.renderRoles()}
                                    </GridItem>
                                </>
                            }
                        </GridContainer>
                    </LayoutContent>
                    <LayoutFooter>
                        <ButtonBar>
                            <LeftButtons>
                                {
                                    !this.isNew &&
                                    <DeleteButton
                                        dialogMessage={this.labels.Platform_User_ConfirmDeleteMsg}
                                        onDelete={() => this.handleDelete()}
                                    />
                                }
                                {
                                    (!this.isNew && this.isNative) &&
                                    <Button
                                        styleType='outlined'
                                        onClick={() => this.setState({ showResetPasswordDialog: true })}
                                    >
                                        {this.labels.Platform_User_ResetPassword}
                                    </Button>
                                }
                            </LeftButtons>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    onClick={() => this.props.navigate(`/security-users`)}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    styleType='filled'
                                    onClick={() => this.handleSave()}
                                >
                                    {this.labels.Platform_CommonAction_Save}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </LayoutFooter>
                    <LayoutRight>
                        <PageNav
                            sections={this.state.sections}
                        />
                    </LayoutRight>
                </LayoutContainer>
                <Dialog
                    isOpen={this.state.showResetPasswordDialog}
                    onOpenChange={() => this.setState({ showResetPasswordDialog: false })}
                >
                    <DialogContent>
                        <Card
                            title={this.labels.HubLabelConfirmation}
                        >
                            {this.labels.Platform_User_ResetPassword}
                        </Card>
                    </DialogContent>
                    <DialogFooter>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    styleType={'outlined'}
                                    onClick={() => this.setState({ showResetPasswordDialog: false })}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    onClick={() => this.handleResetPassword()}
                                >
                                    {this.labels.Platform_User_ResetPassword}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </DialogFooter>
                </Dialog>
            </>
        );
    }
}

export default withRouting(EditUser);

export interface IProps extends IRouterProps<IMatchParams> { }

export interface IMatchParams
{
    id: string | undefined;
}

export interface ILocationState
{
    userCreated: boolean;
    applicationKey?: string;
}

export interface IState
{
    showResetPasswordDialog: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    canViewUserPreferences: boolean;
    sections: IPageNavSection[];

    email: string;
    emailError: string;
    identityProviderOptions: { label: string, value: string }[];
    identityProviderId: number;
    userTypeOptions: { label: string, value: UserType }[];
    userType: UserType;
    preferredName: string;
    firstName: string;
    firstNameError: string;
    lastName: string;
    lastNameError: string;
    displayName: string;
    displayNameError: string;
    initials: string;
    phoneNumber: string;
    location: string;
    password: string;
    passwordError: string;
    applicationKey: string;
    isFullApplicationKey: boolean;
    userPreferences: string;
    userPreferencesError: string;
    etag: string;
}
